import type React from "react";
import { AssetLogo } from "./AssetLogo";
import {
  codingAgents,
  coverLogos,
  modelCards,
  showcaseAssets,
} from "./SlideData";

const mutedText = "text-white/55";
const subMutedText = "text-white/45";

function Slide01Cover() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(circle_at_18%_10%,rgba(34,211,238,0.14),transparent_34%),radial-gradient(circle_at_78%_0%,rgba(59,130,246,0.2),transparent_38%),linear-gradient(145deg,#06153a,#020a21)] p-4 sm:p-6">
      <div className="pointer-events-none absolute inset-0 opacity-50 [background:linear-gradient(120deg,transparent_20%,rgba(255,255,255,0.06)_48%,transparent_70%)]" />
      <div className="relative grid gap-6 lg:grid-cols-[1.02fr_1fr] lg:items-center">
        <div className="order-2 lg:order-1">
          <div className="mb-6 flex flex-wrap items-center gap-2.5">
            <span className="rounded-full border border-white/35 bg-white/10 px-4 py-2 text-xs font-medium tracking-wide text-white/90">Seed Round Deck</span>
            <span className="rounded-full border border-white/35 bg-white/10 px-4 py-2 text-xs font-medium tracking-wide text-white/90">2026</span>
          </div>
          <div className="mb-5 text-cyan-100/90">↗</div>
          <h2 className="text-[clamp(2rem,7vw,4.6rem)] font-semibold leading-[0.96] tracking-[-0.03em] text-white">
            Open AI
            <br />
            Infrastructure
          </h2>
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.22em] text-white/80 sm:text-base">
            Presentation Overview
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Open models, India-first infra, and enterprise-grade serving for teams
            that need performance without lock-in.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            {coverLogos.map((logo) => (
              <div
                key={logo.alt}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/10 p-2 sm:h-12 sm:w-12"
              >
                <AssetLogo src={logo.src} alt={logo.alt} className="max-h-full max-w-full" />
              </div>
            ))}
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <div className="mx-auto max-w-[520px] rounded-[1.7rem] border border-white/15 bg-black/20 p-2.5 shadow-[0_18px_55px_rgba(0,0,0,0.4)]">
            <div className="overflow-hidden rounded-[1.2rem] border border-white/10">
              <AssetLogo
                src={showcaseAssets.desktopPng}
                alt="Krato AI presentation visual"
                className="h-[360px] w-full object-cover sm:h-[440px] lg:h-[480px]"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-3 pr-1">
            {[
              ["Open Model Infra", "Production-ready"],
              ["India Billing", "UPI + INR"],
            ].map(([head, body]) => (
              <div key={head} className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-right">
                <p className="text-xs font-semibold text-white/90">{head}</p>
                <p className="text-[11px] text-white/60">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="relative mt-5 flex items-center justify-between border-t border-white/10 pt-3">
        <p className="text-xs uppercase tracking-[0.18em] text-white/65">
          Krato AI - Seed Round
        </p>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-cyan-300" />
          <p className="text-xs text-white/70">hello@krato.ai</p>
        </div>
      </div>
    </div>
  );
}

function Slide02Problem() {
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr]">
      <div className="space-y-3">
        {[
          [
            "Dollar billing kills margins",
            "AI costs scale brutally against the rupee. Budgeting becomes unstable.",
          ],
          [
            "Vendor lock-in",
            "A single pricing or policy change can disrupt your roadmap.",
          ],
          [
            "Data residency pressure",
            "Routing sensitive traffic through global infra complicates compliance.",
          ],
          [
            "Low customization",
            "Generic APIs are not optimized for Indian languages and workflows.",
          ],
          [
            "Scaling penalty",
            "Costs rise exactly when your product starts growing.",
          ],
        ].map(([head, body]) => (
          <div key={head} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="font-semibold text-white">{head}</p>
            <p className={`mt-1 text-sm leading-relaxed ${subMutedText}`}>{body}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-6 text-lg font-medium leading-relaxed text-white/80">
        Indian developers are building on infrastructure they do not own, cannot
        customize, and can barely afford. This is not only a pricing issue. It
        is a sovereignty issue.
      </div>
    </div>
  );
}

function Slide03Solution() {
  return (
    <div className="space-y-7">
      <p className={`max-w-4xl text-xl leading-relaxed ${mutedText}`}>
        Krato AI deploys leading open models with OpenAI and Anthropic compatible
        APIs, so teams can migrate in minutes. Pay in INR via UPI, keep data in
        India, and scale without platform lock-in.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          "Open model serving, production ready",
          "High throughput inference",
          "OpenAI and Anthropic compatible API",
          "Enterprise security and reliability",
          "Pluggable RAG and tool integrations",
          "INR billing via UPI",
        ].map((item) => (
          <div
            key={item}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70"
          >
            <span className="text-cyan-300">*</span>
            {item}
          </div>
        ))}
      </div>
      <p className="text-2xl font-semibold text-cyan-200">
        Your stack. Your data. Your rules.
      </p>
    </div>
  );
}

function Slide04Models() {
  return (
    <div className="space-y-7">
      <p className={`max-w-4xl text-lg leading-relaxed ${mutedText}`}>
        We do not only host models. We harden them with high-throughput serving,
        auto-scaling, SLA-backed uptime, and continuous updates as the open model
        ecosystem evolves.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {modelCards.map(([logo, name, desc]) => (
          <div key={name} className="rounded-2xl border border-white/10 bg-[#111127]/80 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 p-2">
              <AssetLogo src={logo} alt={name} className="max-h-full max-w-full" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-white sm:text-lg">{name}</h3>
            <p className={`mt-2 text-sm ${subMutedText}`}>{desc}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-5">
        {[
          ["99.9%", "Uptime SLA"],
          ["70%", "Lower than GPT-4 class pricing"],
          ["<100ms", "P50 latency"],
          ["Auto", "Scaling"],
          ["SOC 2", "Aligned"],
        ].map(([value, label]) => (
          <div key={label} className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 p-4 text-center">
            <p className="text-2xl font-black text-white">{value}</p>
            <p className={`mt-1 text-xs ${subMutedText}`}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Slide05ApiCompatibility() {
  return (
    <div className="space-y-6">
      <p className={`max-w-4xl text-lg leading-relaxed ${mutedText}`}>
        Drop-in compatibility with OpenAI and Anthropic formats. Update base URL
        and API key, keep your existing agents, tools, and orchestration flows.
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        {[
          ["No code changes", "Switch providers by changing base URL and key only"],
          ["API compatible", "Request and response format aligned with common SDKs"],
          ["Future proof", "Continuously updated model catalog and endpoints"],
        ].map(([head, body]) => (
          <div key={head} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="font-semibold text-white">{head}</p>
            <p className={`mt-1 text-sm ${subMutedText}`}>{body}</p>
          </div>
        ))}
      </div>
      <p className="text-center text-2xl font-semibold text-cyan-200">
        We reduced switching cost to near zero.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <pre className="overflow-x-auto rounded-2xl border border-white/10 bg-black/35 p-5 text-sm text-white/70">
          <code>
            POST https://api.krato.ai/v1/chat/completions{"\n"}
            model: "krato/llama-3.1-70b-instruct"
          </code>
        </pre>
        <pre className="overflow-x-auto rounded-2xl border border-white/10 bg-black/35 p-5 text-sm text-white/70">
          <code>
            POST https://api.krato.ai/v1/messages{"\n"}
            model: "krato/deepseek-r1"
          </code>
        </pre>
      </div>
      <p className="text-center text-sm text-white/40">
        /v1/chat/completions | /v1/messages | /v1/models | /v1/embeddings
      </p>
    </div>
  );
}

function Slide06Rag() {
  return (
    <div className="space-y-7">
      <p className={`max-w-4xl text-lg leading-relaxed ${mutedText}`}>
        Generic AI hallucinates. Grounded AI ships outcomes. Krato AI's pluggable
        RAG connects your docs, data stores, and repositories with hybrid search,
        code-aware retrieval, and connector-based ingestion.
      </p>
      <div className="grid gap-2 md:grid-cols-6">
        {[
          ["Ingest", "Load source data"],
          ["Chunk", "Structure content"],
          ["Retrieve", "Hybrid search and rerank"],
          ["Augment", "Inject context"],
          ["Generate", "Call model endpoint"],
          ["Respond", "Reliable outputs"],
        ].map(([head, body]) => (
          <div key={head} className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 p-4 text-center">
            <p className="font-semibold text-white">{head}</p>
            <p className={`mt-1 text-xs ${subMutedText}`}>{body}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {[
          ["50+ connectors", "Databases, object stores, docs, and collaboration tools"],
          ["Advanced retrieval", "Vector + lexical search, filtering, and query rewriting"],
          ["Built for code", "Repo-aware indexing and codebase-level context retrieval"],
        ].map(([head, body]) => (
          <div key={head} className="rounded-2xl border border-white/10 bg-[#111127]/80 p-5">
            <h3 className="font-semibold text-white">{head}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${subMutedText}`}>{body}</p>
          </div>
        ))}
      </div>
      <p className="text-2xl font-semibold text-cyan-200">
        Your data. Your rules. Your advantage.
      </p>
    </div>
  );
}

function Slide07Ecosystem() {
  return (
    <div className="space-y-7">
      <p className={`max-w-4xl text-lg leading-relaxed ${mutedText}`}>
        Krato AI integrates with tools teams already use. Keep your developer
        workflows and replace only the model-serving layer.
      </p>
      <div>
        <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-cyan-300/70">
          Coding Agents
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {codingAgents.map(([logo, head, body]) => (
            <div key={head} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <AssetLogo
                src={logo}
                alt={head}
                className="mb-3 h-8 w-8 brightness-0 invert"
              />
              <p className="font-semibold text-white">{head}</p>
              <p className={`mt-1 text-sm ${subMutedText}`}>{body}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <p className="mb-2 font-semibold text-white">Agent Frameworks</p>
        <p className="text-white/50">
          LangChain | Mastra | CrewAI | AutoGen | LlamaIndex | Haystack | and
          more
        </p>
      </div>
      <div className="flex flex-col gap-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-5 sm:flex-row sm:items-center sm:p-6">
        <AssetLogo
          src={showcaseAssets.upiSvg}
          alt="UPI"
          className="h-10 w-auto max-w-28 brightness-0 invert"
        />
        <div>
          <p className="text-lg font-semibold text-white sm:text-xl">
            Pay in INR. The way India pays.
          </p>
          <p className="mt-2 text-sm text-white/55 sm:text-base">
            No forex dependency. No international card friction. Instant UPI
            activation with transparent billing.
          </p>
        </div>
      </div>
    </div>
  );
}

function Slide08Economics() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <AssetLogo src={showcaseAssets.paymentIcon} alt="Payment" className="h-10 w-10" />
        <p className={`text-base sm:text-lg ${mutedText}`}>
          One NVIDIA B200 GPU. Approximate unit economics shown for planning and
          pricing clarity.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {[
          ["~3,500 tok/s", "B200 throughput"],
          ["~12.6M tokens/hr", "Tokens per hour"],
          ["INR 470/hr", "GPU cost (at $5/hr, INR 94/$)"],
        ].map(([value, label]) => (
          <div key={label} className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-5 text-center">
            <p className="text-3xl font-black text-white">{value}</p>
            <p className={`mt-1 text-sm ${subMutedText}`}>{label}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="bg-white/5 text-white/65">
            <tr>
              <th className="p-4">Metric</th>
              <th className="p-4">Per hour</th>
              <th className="p-4">Per month (80% utilization)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10 text-white/55">
            {[
              ["Revenue", "INR 1,260", "INR 7.26L"],
              ["GPU cost", "INR 470", "INR 2.71L"],
              ["Gross profit", "INR 790", "INR 4.55L"],
              ["Gross margin", "63%", "63%"],
            ].map((row) => (
              <tr key={row[0]}>
                <td className="p-4 font-semibold text-white/80">{row[0]}</td>
                <td className="p-4">{row[1]}</td>
                <td className="p-4">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-center text-lg font-semibold text-white">
        At 10 GPUs: Revenue INR 72.6L/month | GPU cost INR 27.1L/month | Gross
        profit INR 45.5L/month
      </div>
      <p className="text-xs leading-relaxed text-white/40">
        Networking, storage, and ops typically add 15-20% cost overhead.
        Long-term margin improves with local infrastructure partnerships.
      </p>
    </div>
  );
}

function Slide09Vision() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_0.85fr]">
      <div className="space-y-6">
        <p className={`text-lg leading-relaxed ${mutedText}`}>
          Proprietary AI creates dependence. Open AI creates leverage. Indian
          developers should own more of their AI stack, control data movement,
          and retain more of the value they generate.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Performance at scale",
            "Open and transparent",
            "Plug, play, and extend",
            "Developer first",
            "India-first pricing",
            "Enterprise ready",
          ].map((point) => (
            <div key={point} className="rounded-xl border border-white/10 bg-white/5 p-4 text-white/70">
              {point}
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-6 text-lg leading-relaxed text-white/75">
        India has one of the largest developer communities globally. AI inference
        is one of the fastest-growing infrastructure categories. The market needs
        an open-model inference platform built for Indian teams.
      </div>
    </div>
  );
}

function Slide10Plan() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-4">
        {[
          [
            "Phase 1 - Infrastructure (Month 1-2)",
            [
              "Lease initial B200 GPU capacity",
              "Deploy core open model lineup",
              "Launch OpenAI and Anthropic compatible endpoints",
            ],
          ],
          [
            "Phase 2 - Product (Month 2-4)",
            [
              "UPI billing and INR dashboard",
              "RAG pipeline with connectors",
              "Developer docs, SDKs, onboarding",
            ],
          ],
          [
            "Phase 3 - Traction (Month 4-6)",
            [
              "Onboard 50-100 design partner startups",
              "Validate real utilization economics",
              "Prepare Series A readiness metrics",
            ],
          ],
        ].map(([phase, items]) => (
          <div key={phase as string} className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <h3 className="font-semibold text-white">{phase as string}</h3>
            <ul className="mt-3 space-y-1.5 text-sm text-white/55">
              {(items as string[]).map((item) => (
                <li key={item}>- {item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-6">
        <h3 className="mb-5 text-xl font-semibold text-white">Use of funds</h3>
        {[
          ["GPU capacity (lease)", "40%"],
          ["Engineering (core hires)", "35%"],
          ["Infrastructure and DevOps", "15%"],
          ["GTM and design partners", "10%"],
        ].map(([label, pct]) => (
          <div key={label} className="mb-4">
            <div className="mb-1 flex justify-between text-sm text-white/70">
              <span>{label}</span>
              <span>{pct}</span>
            </div>
            <div className="h-2 rounded-full bg-white/10">
              <div className="h-2 rounded-full bg-cyan-300" style={{ width: pct }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Slide11Closing() {
  return (
    <div className="space-y-8">
      <p className="max-w-4xl text-3xl font-semibold leading-tight text-cyan-100">
        Democratize AI. Empower developers. Build India's AI future on open
        foundations.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          "hello@krato.ai",
          "docs.krato.ai",
          "api.krato.ai",
          "discord.gg/kratoai | @krato_ai",
        ].map((item) => (
          <div key={item} className="rounded-xl border border-white/10 bg-white/5 p-4 text-white/65">
            {item}
          </div>
        ))}
      </div>
      <p className="text-lg text-white/55">
        Early stage. Large market gap. Clear infrastructure opportunity.
      </p>
    </div>
  );
}

export const slideBlocks: React.ReactNode[] = [
  <Slide01Cover key="slide-1" />,
  <Slide02Problem key="slide-2" />,
  <Slide03Solution key="slide-3" />,
  <Slide04Models key="slide-4" />,
  <Slide05ApiCompatibility key="slide-5" />,
  <Slide06Rag key="slide-6" />,
  <Slide07Ecosystem key="slide-7" />,
  <Slide08Economics key="slide-8" />,
  <Slide09Vision key="slide-9" />,
  <Slide10Plan key="slide-10" />,
  <Slide11Closing key="slide-11" />,
];
